import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui';
import './Header.css';
import mslogo from '../assets/images/MSMlogo_white.png';

const MARKETING_DESKTOP_LINKS = [
    { label: 'About', to: '/about' },
    { label: 'Services', to: '/services' },
    { label: 'Contact', to: '/contact' },
];

const MARKETING_DRAWER_LINKS = [
    { label: 'Home', to: '/' },
    ...MARKETING_DESKTOP_LINKS,
];

const maintenanceLinks = [
    { label: 'Rules', to: '/admin/rules' },
    { label: 'Fields', to: '/admin/fields' },
    { label: 'Static Data', to: '/admin/staticdata' },
    { label: 'Client Admin', to: '/admin/client/CL001' },
    { label: 'Fieldmapping Admin', to: '/admin/mappings' },
];

const activityLinks = [
    { label: 'Entity Matching', to: '/entity-matching' },
    { label: 'Match Resolution', to: '/resolve' },
    { label: 'Voice Assistant', to: '/voice' },
    { label: 'Rule Engine', to: '/dashboard' },
];

const Header = ({
    onLogout,
    variant = 'app',
    buttons = [],
    showLoginButtonOnly,
}) => {
    const effectiveVariant = showLoginButtonOnly ? 'minimal' : variant;
    const [menuOpen, setMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const drawerRef = useRef(null);
    const [user] = useAuthState(auth);
    const navigate = useNavigate();
    const isAuthenticated = !!user;

    const toggleMenu = () => setMenuOpen((prev) => !prev);
    const closeMenu = () => setMenuOpen(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (drawerRef.current && !drawerRef.current.contains(e.target)) {
                closeMenu();
            }
        };
        if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen]);

    const publicButtons = buttons.length
        ? buttons
        : [{ label: 'Login', to: '/login' }];

    const handleDrawerLogout = async () => {
        closeMenu();
        if (onLogout) {
            await onLogout();
        } else {
            await auth.signOut();
            navigate('/');
        }
    };

    const navLinkClass = ({ isActive }) =>
        `header-nav-link${isActive ? ' header-nav-link--active' : ''}`;

    if (effectiveVariant === 'minimal') {
        return (
            <motion.header
                className={`header header--minimal ${isScrolled ? 'header--scrolled' : ''}`}
                initial={{ y: -100 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <Link to="/" className="header-logo-link">
                    <img src={mslogo} alt="MotionSpace" className="header-logo" />
                </Link>
                <div className="header-right">
                    <Link to="/" className="header-minimal-link">
                        Back to site
                    </Link>
                </div>
            </motion.header>
        );
    }

    if (effectiveVariant === 'marketing') {
        return (
            <motion.header
                className={`header header--marketing ${isScrolled ? 'header--scrolled' : ''} ${
                    isAuthenticated ? 'header--has-user' : ''
                }`}
                initial={{ y: -100 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <Link to="/" className="header-logo-link">
                    <img src={mslogo} alt="MotionSpace" className="header-logo" />
                </Link>

                <nav className="header-nav-desktop" aria-label="Primary">
                    {MARKETING_DESKTOP_LINKS.map(({ label, to }) => (
                        <NavLink key={to} to={to} className={navLinkClass}>
                            {label}
                        </NavLink>
                    ))}
                </nav>

                <div className="header-right header-right--marketing">
                    {!isAuthenticated && (
                        <Link to="/login" className="header-cta-button header-login-desktop">
                            <Button variant="outline" size="sm">
                                Login
                            </Button>
                        </Link>
                    )}
                    <button
                        type="button"
                        className="hamburger-icon header-marketing-menu"
                        onClick={toggleMenu}
                        aria-label="Open menu"
                        aria-expanded={menuOpen}
                    >
                        <span className="hamburger-line" />
                        <span className="hamburger-line" />
                        <span className="hamburger-line" />
                    </button>
                </div>

                <AnimatePresence>
                    {menuOpen && (
                        <>
                            <motion.div
                                className="drawer-overlay"
                                onClick={closeMenu}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                            />
                            <motion.div
                                className={`side-drawer ${menuOpen ? 'open' : ''}`}
                                ref={drawerRef}
                                initial={{ x: '100%' }}
                                animate={{ x: 0 }}
                                exit={{ x: '100%' }}
                                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            >
                                <button
                                    type="button"
                                    className="close-icon"
                                    onClick={closeMenu}
                                    aria-label="Close menu"
                                >
                                    ×
                                </button>

                                <div className="drawer-section-label">Explore</div>
                                {MARKETING_DRAWER_LINKS.map(({ label, to }, index) => (
                                    <Link
                                        key={index}
                                        to={to}
                                        onClick={closeMenu}
                                        className="drawer-link"
                                    >
                                        {label}
                                    </Link>
                                ))}

                                {!isAuthenticated && (
                                    <Link
                                        to="/login"
                                        onClick={closeMenu}
                                        className="drawer-link drawer-link--emphasis"
                                    >
                                        Login
                                    </Link>
                                )}

                                {isAuthenticated && (
                                    <>
                                        <div className="drawer-section-label">Workspace</div>
                                        {maintenanceLinks.map(({ label, to }, index) => (
                                            <Link
                                                key={index}
                                                to={to}
                                                onClick={closeMenu}
                                                className="drawer-link"
                                            >
                                                {label}
                                            </Link>
                                        ))}
                                        <div className="drawer-section-label drawer-section-label--sub">
                                            Activity
                                        </div>
                                        {activityLinks.map(({ label, to }, index) => (
                                            <Link
                                                key={index}
                                                to={to}
                                                onClick={closeMenu}
                                                className="drawer-link"
                                            >
                                                {label}
                                            </Link>
                                        ))}
                                        <button
                                            type="button"
                                            className="drawer-link logout"
                                            onClick={handleDrawerLogout}
                                        >
                                            Logout
                                        </button>
                                    </>
                                )}
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </motion.header>
        );
    }

    /* variant === 'app' */
    return (
        <motion.header
            className={`header ${isScrolled ? 'header--scrolled' : ''}`}
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Link to="/" className="header-logo-link">
                <img src={mslogo} alt="MotionSpace" className="header-logo" />
            </Link>

            <div className="header-right">
                <button
                    type="button"
                    className="hamburger-icon"
                    onClick={toggleMenu}
                    aria-label="Toggle menu"
                    aria-expanded={menuOpen}
                >
                    <span className="hamburger-line" />
                    <span className="hamburger-line" />
                    <span className="hamburger-line" />
                </button>

                <AnimatePresence>
                    {menuOpen && (
                        <>
                            <motion.div
                                className="drawer-overlay"
                                onClick={closeMenu}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                            />
                            <motion.div
                                className={`side-drawer ${menuOpen ? 'open' : ''}`}
                                ref={drawerRef}
                                initial={{ x: '100%' }}
                                animate={{ x: 0 }}
                                exit={{ x: '100%' }}
                                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            >
                                <button
                                    type="button"
                                    className="close-icon"
                                    onClick={closeMenu}
                                    aria-label="Close menu"
                                >
                                    ×
                                </button>

                                {isAuthenticated ? (
                                    <>
                                        <div className="drawer-section-label">Maintenance</div>
                                        {maintenanceLinks.map(({ label, to }, index) => (
                                            <Link
                                                key={index}
                                                to={to}
                                                onClick={closeMenu}
                                                className="drawer-link"
                                            >
                                                {label}
                                            </Link>
                                        ))}

                                        <div className="drawer-section-label">Activity</div>
                                        {activityLinks.map(({ label, to }, index) => (
                                            <Link
                                                key={index}
                                                to={to}
                                                onClick={closeMenu}
                                                className="drawer-link"
                                            >
                                                {label}
                                            </Link>
                                        ))}

                                        {onLogout && (
                                            <button
                                                type="button"
                                                className="drawer-link logout"
                                                onClick={() => {
                                                    onLogout();
                                                    closeMenu();
                                                }}
                                            >
                                                Logout
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    publicButtons.map(({ label, to }, index) => (
                                        <Link
                                            key={index}
                                            to={to}
                                            onClick={closeMenu}
                                            className="drawer-link"
                                        >
                                            {label}
                                        </Link>
                                    ))
                                )}
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </div>
        </motion.header>
    );
};

export default Header;
